import Grid from "@mui/material/Unstable_Grid2";
import { SyntheticEvent } from "react";
import { type FieldValues, type Path } from "react-hook-form";
import type Permissions from "../../types/permission/permission";
import PermissionGroup, {
  PermissionGroupProps,
} from "../permissions/PermissionGroup";
import { useTranslation } from "react-i18next";

export interface PermissionsInputProps<F extends FieldValues> extends Omit<
  PermissionGroupProps,
  "onChange" | "defaultValue" | "label" | "resource" | "checkboxes"
> {
  name: Path<F>;
  onChange: (value: Set<string>) => void;
  onBlur: () => void;
  defaultValue: Set<string>;
  value: Set<string>;
  permissions?: Permissions;
  isLoading?: boolean;
}

const PermissionsInput = <F extends FieldValues>({
  defaultValue,
  onChange,
  onBlur,
  permissions,
  isLoading = false,
  value,
  ...props
}: PermissionsInputProps<F>) => {
  const { t } = useTranslation(["common", "roles"]);
  const handleChange = (
    event: SyntheticEvent<Element, Event>,
    permission: string,
  ) => {
    const checked = (event.currentTarget as HTMLInputElement).checked;
    const newPermissions = new Set<string>(value);
    if (checked) {
      newPermissions.add(permission);
    } else {
      newPermissions.delete(permission);
    }
    onChange(newPermissions);
  };

  if (isLoading) {
    return <>{t("common:loading")}</>;
  }

  const getCheckboxLabel = (value: string) => {
    return value.split(":")[1];
  };

  const interactionPermissions = permissions?.interactionPermissions ?? [];

  return (
    <Grid container spacing={12}>
      <Grid xs={6}>
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={permissions?.auditLogPermissions ?? []}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:auditLogs")}
          resource="auditlog"
        />
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={interactionPermissions}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:interactions")}
          resource="interactions"
        />
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={permissions?.sitePermissions ?? []}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:sites")}
          resource="sites"
        />
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={permissions?.viewPermissions ?? []}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:views")}
          resource="views"
        />
      </Grid>
      <Grid xs={6}>
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={permissions?.customersPermissions ?? []}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:customers")}
          resource="customer"
        />
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={permissions?.rolePermissions ?? []}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:roles")}
          resource="roles"
        />
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={permissions?.userPermissions ?? []}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:users")}
          resource="users"
        />
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={permissions?.connectorPermissions ?? []}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:connectors")}
          resource="connectors"
        />
        <PermissionGroup
          {...props}
          className="mb-4"
          checkboxes={permissions?.summaryTemplatePermissions ?? []}
          getCheckboxLabel={getCheckboxLabel}
          onChange={handleChange}
          onBlur={onBlur}
          value={value}
          label={t("common:summaryTemplates")}
          resource="summary-templates"
        />
      </Grid>
    </Grid>
  );
};

export default PermissionsInput;
